import React, {
  FunctionComponent, useEffect, useState, Fragment,
} from 'react';
import { ConnectedProps, connect } from 'react-redux';
import { SelectionChangedEvent, ColDef } from '@ag-grid-enterprise/all-modules';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { RootState } from 'data/root.reducers';
import { WithAuditDetails, AuditDetails } from 'data/types';
import {
  AfwhitelistPrivileges,
} from 'data/afWhitelist/afWhitelist.types';
import * as actions from 'data/afWhitelist/afWhitelist.actions';
import * as networkActions from 'data/network/network.actions';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import * as selectors from 'data/afWhitelist/afWhitelist.selectors';
import * as networkSelectors from 'data/network/network.selectors';
import {
  AuditActionType,
  AuditService,
  getAuditDetails,
  getAuditFieldInfo,
} from 'shared/userAudit/audit-constants';
import * as supportedFeaturesSelectors from 'data/supportedFeatures/supportedFeatures.selectors';
import { AppToaster } from 'shared/toaster';
import AfWhitelistGrid from './components/AfWhitelistGrid';
import { afWhitelistColumnDefs } from '../../AfWhitelist.type';

type RouterParams = {
  history: string
}

const mapState = (state: RootState) => ({
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  userId: authenticationSelectors.getAuthenticationUserId(state),
  userName: authenticationSelectors.getAuthenticationUserName(state),
  beforeLogout: state.authentication.beforeLogout,
  privileges: state.authentication.privileges,
  data: selectors.getAfWhitelistList(state),
  selectedAfWhitelist: selectors.getSelectedAfWhitelist(state),
});

const mapDispatch = {
  goToAfWhitelistCreate: actions.goToAfWhitelistCreate,
  goToAfWhitelistEdit: actions.goToAfWhitelistEdit,
  goToAfWhitelistView: actions.goToAfWhitelistView,
  selectAfWhitelist: actions.selectAfWhitelist,
  getAfWhitelistList: actions.getAfWhitelistList,
  deleteAfWhitelist: actions.deleteAfWhitelist,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>
type PropsFromRouter = RouteComponentProps<RouterParams>

export type Props = PropsFromRedux & PropsFromRouter

export const AfWhitelistList: FunctionComponent<Props> = ({
  isAuditEnabled,
  userId,
  userName,
  history,
  beforeLogout,
  privileges,
  data,
  selectedAfWhitelist,
  goToAfWhitelistCreate,
  goToAfWhitelistEdit,
  goToAfWhitelistView,
  selectAfWhitelist,
  getAfWhitelistList,
  deleteAfWhitelist,
}: Props) => {
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date>();
  const [columnDefs] = useState<ColDef[]>(afWhitelistColumnDefs);

  useEffect(() => {
    getAfWhitelistList();
    return () => {
      AppToaster.clear();
    };
  }, [getAfWhitelistList]);

  useEffect(() => {
    setLastUpdatedAt(new Date());
  }, [data]);

  const handleRefresh = () => getAfWhitelistList();

  const handleCreateNew = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    goToAfWhitelistCreate(history);
  };

  const handleSelectionChanged = (event: SelectionChangedEvent) => {
    selectAfWhitelist(event.api.getSelectedRows());
  };

  const handleDeleteAfWhitelist = () => {
    const afWhitelistId = selectedAfWhitelist[0]?.id;
    if (afWhitelistId == null) {
      return;
    }

    const body: WithAuditDetails<void> = { data: undefined };

    if (isAuditEnabled) {
      const auditDetails = getAuditDetails(
        isAuditEnabled,
        userId,
        userName,
        getAuditFieldInfo(null),
        selectedAfWhitelist[0]?.id,
        AuditService.AF_WHITELIST,
        AuditActionType.AF_WHITELIST_DELETE,
      );
      body.auditDetails = auditDetails as AuditDetails;
    }

    deleteAfWhitelist(String(afWhitelistId), body);
  };

  const handleGoToEdit = () => goToAfWhitelistEdit(history);

  const handleGoToView = () => goToAfWhitelistView(history);

  const hasPrivilegeToView = privileges.includes(AfwhitelistPrivileges.View);
  const hasPrivilegesToCreate = privileges.includes(AfwhitelistPrivileges.Edit);
  const hasPrivilegeToDelete = privileges.includes(AfwhitelistPrivileges.Delete);

  return (
    <Fragment>
      <AfWhitelistGrid
        data={data}
        hasPrivilegeToView={hasPrivilegeToView}
        hasPrivilegesToCreate={hasPrivilegesToCreate}
        hasPrivilegeToDelete={hasPrivilegeToDelete}
        noResults={false}
        beforeLogout={beforeLogout}
        columnDefs={columnDefs}
        lastUpdatedAt={lastUpdatedAt}
        selectedAfWhitelist={selectedAfWhitelist[0]}
        onRefresh={handleRefresh}
        onCreateNew={handleCreateNew}
        onSelectionChanged={handleSelectionChanged}
        onDelete={handleDeleteAfWhitelist}
        onEdit={handleGoToEdit}
        onView={handleGoToView}
      />
    </Fragment>
  );
};

export default withRouter(connector(AfWhitelistList));
